<template>
  <div>
    <VueDraggable
      v-if="activeBlock && activeBlock.children"
      :key="activeBlock.children.length"
      :model-value="availableNetworks"
      :animation="150"
      group="ntwork"
      item-key="network"
      handle=".drag-handle"
      class="space-y-2"
      @update="onNewtworkDragEnd"
    >
      <network-header
        v-for="(network, i) in availableNetworks"
        :key="network.attributes['name'].toLowerCase().replace('-noshare', '')"
        :network="network"
        :is-custom="isCustomNetwork(network.attributes.name)"
        :supports-share-url="supportsShareUrl(network.attributes.name)"
        :index="i"
        :selected-style="selectedStyle.value"
        :data-testid="`network-${network.attributes.name}-tab`"
        @show="(name) => addCommonNetwork(name)"
        @hide="removeNetworkFromUsed(i)"
      ></network-header>
    </VueDraggable>
    <div style="padding-top: 16px">
      <t-button
        v-if="!addAnotherExpanded"
        key="trigger-button"
        data-testid="add-another-network"
        @click="addCustomNetwork"
      >
        <template #icon>
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.5 6.25C9.77614 6.25 10 6.02614 10 5.75V4.25C10 3.97386 9.77614 3.75 9.5 3.75L6.25 3.75V0.5C6.25 0.223858 6.02614 0 5.75 0L4.25 0C3.97386 0 3.75 0.223858 3.75 0.5V3.75H0.5C0.223858 3.75 0 3.97386 0 4.25V5.75C0 6.02614 0.223858 6.25 0.5 6.25L3.75 6.25L3.75 9.5C3.75 9.77614 3.97386 10 4.25 10H5.75C6.02614 10 6.25 9.77614 6.25 9.5V6.25H9.5Z"
              fill="currentColor"
            />
          </svg>
        </template>
        {{ $t("forms.social_add_custom") }}
      </t-button>
    </div>
  </div>
</template>
<script setup lang="ts">
  import NetworkHeader from "./_internal/NetworkHeader/NetworkHeader.vue";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useSocialNetworksStore } from "@/stores/socialNetworksStore";
  import { TopolSocialBlock } from "@/schemas/template/blocks/socialBlockSchema";

  import { TButton } from "@topol/twig-ui";

  import { VueDraggable } from "vue-draggable-plus";
  import { ref, computed } from "vue";

  interface IProps {
    selectedStyle: { label: string; value: string };
  }

  const props = defineProps<IProps>();

  const addAnotherExpanded = ref(false);

  const mjmlStore = useMjmlStore();
  const socialNetworksStore = useSocialNetworksStore();

  const baseNetworks = [
    "Facebook",
    "Twitter",
    "LinkedIn",
    "Instagram",
    "X",
    "Pinterest",
    "YouTube",
    "Spotify",
    "TikTok",
    "ApplePod",
    "Snapchat",
    "Threads",
  ];

  const supportedShareUrl = ["facebook", "twitter", "pinterest", "linkedin"];

  const activeBlock = computed(
    () => mjmlStore.getActiveBlock as TopolSocialBlock | undefined
  );

  const availableNetworks = computed(() => {
    return socialNetworksStore.getSocialNetworks;
  });

  function removeNetworkFromUsed(index: number) {
    if (!mjmlStore.selectedBlock || !activeBlock.value) {
      return;
    }

    if (activeBlock.value.children.length < 2) {
      //should throw an dialog that cant be removed because of
      return;
    }

    mjmlStore.deleteBlockChild({
      sectionIndex: mjmlStore.selectedBlock.sectionIndex,
      columnIndex: mjmlStore.selectedBlock.columnIndex,
      blockIndex: mjmlStore.selectedBlock.blockIndex,
      childIndex: index,
    });
  }

  function isCustomNetwork(networkName: string) {
    const found = baseNetworks.find(
      (n) =>
        n.toLowerCase() === networkName.toLowerCase().replace("-noshare", "")
    );
    return found === undefined;
  }

  function supportsShareUrl(networkName: string) {
    const found = supportedShareUrl.find(
      (n) =>
        n.toLowerCase() === networkName.toLowerCase().replace("-noshare", "")
    );
    return found !== undefined;
  }

  function onNewtworkDragEnd(evt: {
    oldIndex: number | undefined;
    newIndex: number | undefined;
  }) {
    if (mjmlStore.selectedBlock) {
      mjmlStore.reorderBlockChildren({
        sectionIndex: mjmlStore.selectedBlock.sectionIndex,
        columnIndex: mjmlStore.selectedBlock.columnIndex,
        blockIndex: mjmlStore.selectedBlock.blockIndex,
        oldIndex: evt.oldIndex,
        newIndex: evt.newIndex,
      });
    }
  }

  function addCommonNetwork(network: string) {
    network = network.toLowerCase();
    const nameWithoutNoShare = network.replace("-noshare", "");

    const src =
      "https://s3-eu-west-1.amazonaws.com/ecomail-assets/editor/social-icos/" +
      props.selectedStyle.value +
      "/" +
      nameWithoutNoShare +
      ".png";

    //when adding a social, make sure it's noshare by default (only when social supports share URLs)
    if (supportedShareUrl.includes(network)) {
      network += "-noshare";
    }

    addNewNetwork(src, network);
  }

  function addCustomNetwork() {
    if (!activeBlock.value) {
      return;
    }

    const src = "https://topolio.s3.eu-west-1.amazonaws.com/editor/world.png";

    const num = activeBlock.value.children.filter(
      (socialElement: {
        attributes: {
          name: string;
        };
      }) => {
        if (
          socialElement &&
          socialElement.attributes["name"].indexOf("custom") != -1
        )
          return socialElement;
      }
    ).length;

    addNewNetwork(src, "custom-" + (num + 1));
  }

  function addNewNetwork(src: string, networkName: string) {
    if (
      !mjmlStore.selectedBlock ||
      !activeBlock.value ||
      !activeBlock.value.children
    ) {
      return;
    }

    mjmlStore.addBlockChild({
      sectionIndex: mjmlStore.selectedBlock.sectionIndex,
      columnIndex: mjmlStore.selectedBlock.columnIndex,
      blockIndex: mjmlStore.selectedBlock.blockIndex,
      childIndex: activeBlock.value.children.length,
      value: {
        tagName: "mj-social-element",
        attributes: {
          src: src,
          name: networkName,
          alt: "",
          href: networkName,
          "background-color": "transparent",
        },
      },
    });
  }
</script>
<style lang="scss"></style>
